import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Search, Pencil, Archive, Trash2, RotateCcw, Download,
  Sparkles, Utensils, MapPin, Clock,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import api from "../../lib/api";
import { useRestaurant } from "../../context/RestaurantContext";
import RestaurantEditDialog from "./RestaurantEditDialog";
import ArchiveConfirmDialog from "./ArchiveConfirmDialog";
import DeleteOTPDialog from "./DeleteOTPDialog";
import ArchivedCountdown from "./ArchivedCountdown";

const normalize = (s) =>
  (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};

export default function RestaurantsSettings({ currentRestaurantId }) {
  const { refreshRestaurants, selectRestaurant, restaurants: contextRestaurants } = useRestaurant();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get("archived") === "1" ? "archived" : "active"
  );

  const [active, setActive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [target, setTarget] = useState(null);  // restaurant being acted on
  const [exportingId, setExportingId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.getRestaurants(),  // active only
        api.getArchivedRestaurants().catch(() => []),
      ]);
      setActive(a || []);
      setArchived(b || []);
    } catch (err) {
      toast.error(err.message || "Error al cargar restaurantes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Keep active list in sync with the global context (in case other pages mutate)
  useEffect(() => {
    if (contextRestaurants?.length) {
      setActive(contextRestaurants);
    }
  }, [contextRestaurants]);

  const activeFiltered = useMemo(() => {
    const q = normalize(search);
    if (!q) return active;
    return active.filter(r => {
      const hay = [r.name, r.address, r.city, r.country, r.cuisine_type, r.business_type]
        .map(normalize).join(" ");
      return hay.includes(q);
    });
  }, [active, search]);

  const archivedFiltered = useMemo(() => {
    const q = normalize(search);
    if (!q) return archived;
    return archived.filter(r => {
      const hay = [r.name, r.address, r.city, r.country, r.cuisine_type]
        .map(normalize).join(" ");
      return hay.includes(q);
    });
  }, [archived, search]);

  const handleArchiveDone = async (r) => {
    // If we archived the current restaurant, fall back to another active
    const others = active.filter(x => x.restaurant_id !== r.restaurant_id);
    if (r.restaurant_id === currentRestaurantId && others.length > 0) {
      selectRestaurant(others[0]);
    }
    await refreshRestaurants();
    await loadAll();
    setActiveTab("archived");
    setSearchParams({ tab: "restaurants", archived: "1" });
  };

  const handleRestoreDone = async () => {
    await refreshRestaurants();
    await loadAll();
  };

  const handleDeleteDone = async () => {
    await refreshRestaurants();
    await loadAll();
  };

  const handleExport = async (r) => {
    setExportingId(r.restaurant_id);
    try {
      const res = await api.exportRestaurantDataBlob(r.restaurant_id);
      const url = URL.createObjectURL(res);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.web_slug || r.name || "restaurante"}-export.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export descargado");
    } catch (err) {
      toast.error(err.message || "Error al exportar");
    } finally {
      setExportingId(null);
    }
  };

  const handleRestore = async (r) => {
    try {
      await api.restoreRestaurant(r.restaurant_id);
      toast.success(`${r.name} restaurado`);
      await handleRestoreDone();
    } catch (err) {
      toast.error(err.message || "Error al restaurar");
    }
  };

  const openEdit = (r) => { setTarget(r); setEditOpen(true); };
  const openArchive = (r) => { setTarget(r); setArchiveOpen(true); };
  const openDelete = (r) => { setTarget(r); setDeleteOpen(true); };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="restaurants-settings">
      <div>
        <h2 className="font-heading text-xl font-semibold">Mis restaurantes</h2>
        <p className="text-muted-foreground text-sm">
          Gestiona, archiva o elimina tus restaurantes. Los archivados se eliminan automáticamente a los 90 días.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, dirección, ciudad o tipo de cocina…"
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="restaurants-search"
        />
      </div>

      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        setSearchParams({ tab: "restaurants", ...(v === "archived" ? { archived: "1" } : {}) });
      }}>
        <TabsList data-testid="restaurants-tabs">
          <TabsTrigger value="active" data-testid="sub-tab-active">
            Activos ({activeFiltered.length})
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="sub-tab-archived">
            Archivados ({archivedFiltered.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4 space-y-3">
          {activeFiltered.length === 0 && (
            <Card className="border-dashed border-2">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {active.length === 0 ? "No tienes restaurantes activos." : "Ningún restaurante coincide con la búsqueda."}
              </CardContent>
            </Card>
          )}
          {activeFiltered.map(r => {
            const isOwner = r.role === "owner";
            const isCurrent = r.restaurant_id === currentRestaurantId;
            return (
              <Card key={r.restaurant_id} data-testid={`restaurant-card-${r.restaurant_id}`} className="border shadow-sm">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-foreground truncate">{r.name}</h3>
                      {isCurrent && (
                        <span className="inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                          Activo ahora
                        </span>
                      )}
                      <span className="inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-muted text-foreground border border-border">
                        {r.role === "owner" ? "Dueño" : r.role}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {r.cuisine_type && (
                        <span className="inline-flex items-center gap-1"><Utensils className="h-3 w-3" />{r.cuisine_type}</span>
                      )}
                      {(r.city || r.address) && (
                        <span className="inline-flex items-center gap-1 truncate max-w-[320px]">
                          <MapPin className="h-3 w-3" />{r.city || r.address}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`restaurant-edit-${r.restaurant_id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExport(r)}
                      disabled={exportingId === r.restaurant_id}
                      data-testid={`restaurant-export-${r.restaurant_id}`}
                    >
                      {exportingId === r.restaurant_id
                        ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <Download className="h-3.5 w-3.5 mr-1" />}
                      Exportar
                    </Button>
                    {isOwner && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openArchive(r)}
                        className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                        data-testid={`restaurant-archive-${r.restaurant_id}`}
                      >
                        <Archive className="h-3.5 w-3.5 mr-1" /> Archivar
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="archived" className="mt-4 space-y-3">
          {archivedFiltered.length === 0 && (
            <Card className="border-dashed border-2">
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {archived.length === 0 ? "No tienes restaurantes archivados." : "Ningún restaurante coincide con la búsqueda."}
              </CardContent>
            </Card>
          )}
          {archivedFiltered.map(r => {
            return (
              <Card key={r.restaurant_id} data-testid={`archived-restaurant-card-${r.restaurant_id}`} className="border shadow-sm bg-amber-50/30">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-foreground truncate">{r.name}</h3>
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                        <Clock className="h-3 w-3" /> Archivado
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      <ArchivedCountdown deleteAt={r.delete_at} restaurantId={r.restaurant_id} />
                      <span className="text-muted-foreground">
                        · se elimina el {fmtDate(r.delete_at)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleExport(r)} disabled={exportingId === r.restaurant_id} data-testid={`archived-restaurant-export-${r.restaurant_id}`}>
                      {exportingId === r.restaurant_id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                      Exportar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(r)}
                      className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                      data-testid={`restaurant-restore-${r.restaurant_id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restaurar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDelete(r)}
                      className="text-red-700 hover:text-red-800 hover:bg-red-50"
                      data-testid={`restaurant-delete-${r.restaurant_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Borrar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      <RestaurantEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        restaurant={target}
        onSaved={async () => { await loadAll(); await refreshRestaurants(); }}
      />
      <ArchiveConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        restaurant={target}
        onDone={handleArchiveDone}
      />
      <DeleteOTPDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        restaurant={target}
        onDone={handleDeleteDone}
      />

      <div className="rounded-lg bg-muted/50 border border-dashed p-4 text-xs text-muted-foreground flex gap-2">
        <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-amber-700" />
        <div>
          <strong>Exporta siempre una copia antes de borrar.</strong> Los restaurantes archivados se eliminan automáticamente a los 90 días y no se pueden recuperar después. Si borras manualmente, recibirás un código por email y un aviso de confirmación.
        </div>
      </div>
    </div>
  );
}
